import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlertService } from '../../commons/services/alert.service';
import { AppState } from '../reducers';

import * as IngredientsActions from '../actions/ingredients.actions';
import { getSelectionDialogId, getIngredientsDialogId, getIngredientsTableState } from '../selectors/ingredient.selectors';
import { LaravelIngredientService } from '../../commons/services/laravel/laravel-ingredient.service';
import { NewIngredientComponent } from '../../modules/home/modules/ingredients/components/new-ingredient/new-ingredient.component';
import { DialogComponent } from '../../modules/shared/components/dialog/dialog.component';

@Injectable()
export class IngredientEffects {

  error$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
            IngredientsActions.deleteIngredientFailed,
            IngredientsActions.loadIngredientsFailed
        ),
        tap(({ error }) => {
          if (error) {
            this.alertService.showErrorMessage('Errore', error);
          }
        })
      ),
    { dispatch: false }
  );

  loadIngredients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.loadIngredients),
      // tap(() => console.log("Effetto attivato!")),
      switchMap(({ page, perPage, order, direction, filters, includes }) => {
        // console.log("📤 Chiamata API con:", { page, perPage, order, direction, filters, includes });
        return this.ingredientService
          .list(page, perPage, order, direction, filters, includes)
          .pipe(
            map((result) =>
                IngredientsActions.loadIngredientsCompleted({
                ingredient: result.data,
                currentPage: page,
                total: result.total,
                perPage,
                order,
                direction,
                filters,
                includes
              })
          ),
            catchError((error) =>
              of(IngredientsActions.loadIngredientsFailed({ error }))
            )
          );
      })
    )
  );


  changePage = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.changePage),
      withLatestFrom(this.store$.select(getIngredientsTableState)),
      map(
        ([
          { page, size },
          { total, currentPage, perPage, direction, order, filters, includes },
        ]) =>
            IngredientsActions.loadIngredients({
            page: page,
            perPage: size,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );

  changeSort = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.changeSort),
      withLatestFrom(this.store$.select(getIngredientsTableState)),
      map(
        ([
          action,
          { total, currentPage, perPage, direction, order, filters, includes },
        ]) =>
            IngredientsActions.loadIngredients({
            page: currentPage,
            perPage: perPage,
            order: action.order,
            direction: action.direction,
            filters,
            includes,
          })
      )
    )
  );

  changeFilters = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.changeFilters),
      withLatestFrom(this.store$.select(getIngredientsTableState)),
      map(
        ([
          { filters },
          { total, currentPage, perPage, direction, order, includes },
        ]) =>
            IngredientsActions.loadIngredients({
            page: currentPage,
            perPage: perPage,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );

  editIngredient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.editIngredient),
      map(({ ingredient }) => {
        let dialogRef = this.dialog.open(NewIngredientComponent, {
          data: {
            ingredient,
          },
          panelClass: 'full-screen-dialog',
        });
        return IngredientsActions.ingredientDialogOpened({
          dialogId: dialogRef.id,
        });
      })
    )
  );

  saveIngredient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.saveIngredient),
      mergeMap(({ ingredient }) =>
        this.ingredientService.upsert(ingredient.toDTO()).pipe(
          map((result) =>
            IngredientsActions.saveIngredientCompleted({ ingredient: result })
          ),
          catchError((error) =>
            of(IngredientsActions.saveIngredientFailed({ error }))
          )
        )
      )
    )
  );

  onSaveCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.saveIngredientCompleted),
      map((action) => action.ingredient),
      tap((ingredient) =>
        this.alertService.showConfirmMessage(
          `Ingrediente ${ingredient.name} salvato con successo`
        )
      ),
      map(() => IngredientsActions.closeIngredientsDialog())
    )
  );


  deleteIngredient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.deleteIngredient),
      switchMap(({ ingredient }) =>
        this.alertService
          .showConfirmDialog(
            'Conferma eliminazione',
            `Sei sicuro di voler eliminare l'elemento selezionato?`
          )
          .pipe(
            mergeMap((confirm) => {
              return confirm
                ? this.ingredientService.delete(ingredient.id).pipe(
                    map(() =>
                        IngredientsActions.deleteIngredientCompleted({
                        ingredient,
                      })
                    ),
                    catchError((error) =>
                      of(IngredientsActions.deleteIngredientFailed({ error }))
                    )
                  )
                : of(IngredientsActions.deleteIngredientCancelled());
            })
          )
      )
    )
  );

  onDeleteCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.deleteIngredientCompleted),
      tap(({ ingredient }) =>
        this.alertService.showConfirmMessage(
          `Ingrediente ${ingredient.name} eliminato con successo`
        )
      ),
      map(() => IngredientsActions.closeIngredientsDialog())
    )
  );

  closeDialog = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IngredientsActions.closeIngredientsDialog),
        withLatestFrom(this.store$.select(getIngredientsDialogId)),
        tap(([_, dialogId]) => {
          if (dialogId) {
            this.dialog.getDialogById(dialogId).close();
          }
        })
      ),
    { dispatch: false }
  );

  reloadAfterSave = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.saveIngredientCompleted),
      withLatestFrom(this.store$.select(getIngredientsTableState)),
      map(
        ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
            IngredientsActions.loadIngredients({
            page: currentPage,
            perPage,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );

  reloadAfterDelete = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.deleteIngredientCompleted),
      withLatestFrom(this.store$.select(getIngredientsTableState)),
      map(
        ([_, { currentPage, perPage, direction, order, filters, includes }]) =>
            IngredientsActions.loadIngredients({
            page: currentPage,
            perPage,
            order,
            direction,
            filters,
            includes,
          })
      )
    )
  );


  closeSelectionDialog = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IngredientsActions.closeSelectionDialog),
        withLatestFrom(this.store$.select(getSelectionDialogId)),
        tap(([_, dialogId]) => {
          if (dialogId) {
            this.dialog.getDialogById(dialogId).close();
          }
        })
      ),
    { dispatch: false }
  );

  requestCloseDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.requestCloseIngredientsDialog),
      switchMap(() => {
        const confirmDialogRef = this.dialog.open(DialogComponent, {
          disableClose: false
        });
  
        // Imposta il messaggio del dialog
        confirmDialogRef.componentInstance.title = "Chiudi";
        confirmDialogRef.componentInstance.confirmMessage =
          "Ci sono modifiche non salvate. Sei sicuro di voler chiudere?";
  
        return confirmDialogRef.afterClosed().pipe(
          map(result =>
            result
              ? IngredientsActions.confirmCloseIngredientsDialog()
              : IngredientsActions.cancelCloseIngredientsDialog()
          )
        );
      })
    )
  );
  
  
  confirmCloseDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.confirmCloseIngredientsDialog),
      map(() => IngredientsActions.closeIngredientsDialog())
    )
  );
  
  cancelCloseDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IngredientsActions.cancelCloseIngredientsDialog),
      map(() => {
        // Se l'utente annulla mantiene il dialog aperto
        return { type: 'NO_ACTION' };
      })
    )
  );
  
  closeDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IngredientsActions.closeIngredientsDialog),
        withLatestFrom(this.store$.select(getIngredientsDialogId)),
        tap(([_, dialogId]) => {
          if (dialogId) {
            this.dialog.getDialogById(dialogId).close();
          }
        })
      ),
    { dispatch: false }
  );
    


  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private ingredientService: LaravelIngredientService,
    private dialog: MatDialog,
    private alertService: AlertService
  ) {}
}